import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Eye, EyeOff, Shield, ArrowRight, AlertTriangle } from "lucide-react";
import { AuthLayout, authInputClass, authLinkClass, authSubmitClass } from "@/components/auth/AuthLayout";
import { OAuthButtons } from "@/components/auth/OAuthButtons";
import { gtmPushSignUp } from "@/utils/gtm";
import { zPassword } from "@/utils/passwordPolicy";
import { personNameRefinement } from "@/utils/displayNamePolicy";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { captureInviteCodeFromUrl, getStoredInviteCode, setStoredInviteCode, clearInviteCode, postAuthRedirectFromSearch } from "@/utils/oauth";
import { getAttributionPayload, ensureCountry } from "@/utils/attribution";

const formSchema = z
  .object({
    firstName: z.string().min(2, "First name must be at least 2 characters").superRefine(personNameRefinement("First name")),
    lastName: z.string().min(2, "Last name must be at least 2 characters").superRefine(personNameRefinement("Last name")),
    email: z.string().email("Please enter a valid email"),
    password: zPassword,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

const Signup = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeInvalid, setPromoCodeInvalid] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isAuthenticated, loading: authLoading } = useAuth();
  // Sent with register so the verification email links back to this plan's checkout
  const signupPlan = new URLSearchParams(location.search).get("plan")?.trim() || undefined;
  // An invite only attaches to the address it was sent to, so that address can't be edited here (UAT 1028uhyn F7)
  const signupParams = new URLSearchParams(location.search);
  const inviteEmail = signupParams.get("rd")?.startsWith("/accept-invite") ? signupParams.get("email")?.trim() || "" : "";
  // Handed to the check-email page so it can name the workspace this sign-up joins (UAT 1029l8qb F22)
  const inviteToken = inviteEmail ? new URLSearchParams(signupParams.get("rd")!.split("?")[1] || "").get("token") || undefined : undefined;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      // A team invite link passes the invited email, so the claim lands on the account the invite made
      email: new URLSearchParams(location.search).get("email")?.trim() || "",
      password: "",
      confirmPassword: "",
    },
  });

  // Store redirect URL if present
  useEffect(() => {
    // Riding the rd flow keeps a ?plan= checkout target through email verification and OAuth
    const rd = postAuthRedirectFromSearch(location.search);
    if (rd) {
      localStorage.setItem("post_auth_redirect", rd);
    }
    captureInviteCodeFromUrl();
    // Pre-fill the manual code field from a shared link's ?ref= param, if present —
    // still editable, and this is also what makes manual entry work for OAuth: the
    // OAuth flow reads the same stored value before redirecting to the provider.
    setPromoCode(getStoredInviteCode() || "");
  }, [location.search]);

  // Redirect away if authenticated — covers cross-tab login sync while sitting on /signup.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const storedRedirect = localStorage.getItem("post_auth_redirect");
    if (storedRedirect && storedRedirect.startsWith("/")) {
      localStorage.removeItem("post_auth_redirect");
      navigate(storedRedirect, { replace: true });
    } else {
      navigate("/~", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Feedback only: an unrecognised code never blocks signup, and a failed or rate-limited check shows nothing
  const checkPromoCode = async () => {
    const code = promoCode.trim();
    if (!code) {
      setPromoCodeInvalid(false);
      return;
    }
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/promo-codes/check-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!response.ok) return;
      const data: { valid: boolean } = await response.json();
      setPromoCodeInvalid(data.valid === false);
    } catch {
      setPromoCodeInvalid(false);
    }
  };

  // TEMPORARY: Disable signup functionality
  const SIGNUP_DISABLED = false;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // TEMPORARY: Block signup attempts
    if (SIGNUP_DISABLED) {
      toast({
        variant: "destructive",
        title: "Signup Not Available",
        description: "Signup is temporarily disabled. The platform will be available soon!",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Fill in country if the landing-time lookup has not returned yet; bounded so it
      // cannot delay signup
      await ensureCountry();

      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email,
          password: values.password,
          invite_code: promoCode.trim() || undefined,
          plan: signupPlan,
          attribution: getAttributionPayload(),
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Signup failed';
        try {
          const error = await response.json();
          errorMessage = error.message || error.detail || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      await response.json();
      gtmPushSignUp('email');
      clearInviteCode();

      toast({
        title: "Account created!",
        description: "Please check your email to verify your account before logging in.",
      });

      // Redirect to check email page
      navigate("/check-email", {
        state: { email: values.email, plan: signupPlan, inviteToken }
      });

    } catch (error) {
      toast({
        variant: "destructive",
        title: "Signup Failed",
        description: error instanceof Error ? error.message : "Failed to create account. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSSOClick = () => {
    // TEMPORARY: Block SSO signup attempts
    if (SIGNUP_DISABLED) {
      toast({
        variant: "destructive",
        title: "Signup Not Available",
        description: "Signup is temporarily disabled. The platform will be available soon!",
      });
      return;
    }

    toast({
      title: "Coming Soon",
      description: "SSO authentication will be available in a future update.",
    });
  };

  return (
    <AuthLayout
      title="Make room for your next idea."
      description="Create your workspace and start building with Joylo."
    >
      {SIGNUP_DISABLED && (
      <div className="mb-6">
        <div>
          <div className="flex items-center space-x-3 rounded-xl px-4 py-2 bg-warning-bg border border-warning/20">
            <div className="flex items-center justify-center h-8 w-12 rounded-full bg-gradient-to-br from-amber-400/80 to-orange-400/80 mr-2 animate-bounce">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div className="text-sm text-amber-900 leading-relaxed">
              <span className="font-bold text-amber-700">Something Revolutionary Is Coming:</span> We're building <span className="font-bold text-[#9b87f5]">Joylo</span> — a platform that will change the way apps are created.
              <span className="mx-1 text-orange-500">⚠️</span> This is our <strong>test playground</strong>. <span className="font-semibold text-amber-700">Please <u>do not sign up or enter personal information</u></span> — it's not the real thing yet! The official launch is coming soon!
            </div>
          </div>
        </div>
      </div>
    )}

      <OAuthButtons isSignupPage={true} disableSignup={SIGNUP_DISABLED} />

      <button
        type="button"
        onClick={handleSSOClick}
        disabled={SIGNUP_DISABLED}
        className="mx-auto mt-4 flex items-center gap-2 text-[13px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
      >
        <Shield className="h-3.5 w-3.5" />
        Enterprise SSO
        <ArrowRight className="h-3.5 w-3.5" />
      </button>

      <div className="my-6 flex items-center gap-4 text-[12px] text-muted-foreground">
        <span className="h-px flex-1 bg-line" />
        or sign up with email
        <span className="h-px flex-1 bg-line" />
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-[13px] font-semibold text-foreground">First Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John"
                      {...field}
                      className={authInputClass}
                      disabled={SIGNUP_DISABLED}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-[13px] font-semibold text-foreground">Last Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Doe"
                      {...field}
                      className={authInputClass}
                      disabled={SIGNUP_DISABLED}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[13px] font-semibold text-foreground">Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="your@email.com"
                    {...field}
                    className={authInputClass}
                    disabled={SIGNUP_DISABLED}
                    readOnly={!!inviteEmail}
                    aria-describedby={inviteEmail ? "invite-email-note" : undefined}
                  />
                </FormControl>
                {inviteEmail && (
                  <p id="invite-email-note" className="text-xs text-muted-foreground">
                    Your invite was sent to this address.{" "}
                    <Link
                      to="/signup"
                      className={authLinkClass}
                      onClick={() => {
                        // Someone else signing up must not be sent back to this invite afterwards
                        try { localStorage.removeItem("post_auth_redirect"); } catch { /* storage unavailable */ }
                        form.setValue("email", "");
                      }}
                    >
                      Not you?
                    </Link>
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[13px] font-semibold text-foreground">Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      {...field}
                      className={`${authInputClass} pr-11`}
                      disabled={SIGNUP_DISABLED}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0.5 top-0.5 h-10 w-10 hover:bg-soft"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={SIGNUP_DISABLED}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <PasswordRequirements password={field.value || ""} />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[13px] font-semibold text-foreground">Confirm Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      {...field}
                      className={`${authInputClass} pr-11`}
                      disabled={SIGNUP_DISABLED}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0.5 top-0.5 h-10 w-10 hover:bg-soft"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={SIGNUP_DISABLED}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div>
            <label className="text-[13px] font-semibold text-foreground text-sm">
              Invite or promo code <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <Input
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value);
                setStoredInviteCode(e.target.value);
                setPromoCodeInvalid(false);
              }}
              onBlur={checkPromoCode}
              aria-label="Invite or promo code"
              aria-invalid={promoCodeInvalid}
              className={`${authInputClass} mt-1`}
              disabled={SIGNUP_DISABLED}
            />
            {promoCodeInvalid && (
              <p className="mt-1 text-[13px] font-medium text-destructive" role="alert">
                This code isn't valid. You can still create your account without it.
              </p>
            )}
          </div>

          <button
            type="submit"
            className={authSubmitClass}
            disabled={isLoading || SIGNUP_DISABLED}
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isLoading ? "Creating account..." : "Create account"}
          </button>
        </form>
      </Form>

      <p className="mt-6 text-center text-[13px] text-muted-foreground">
        Already have an account?{" "}
        {/* Keep ?plan=/?rd= so an existing customer who clicked a plan CTA still lands on checkout */}
        <Link to={{ pathname: "/login", search: location.search }} className={authLinkClass}>
          Sign in
        </Link>
      </p>

      <p className="mt-4 text-center text-[12px] text-muted-foreground">
        By creating an account, you agree to our{" "}
        <a href="/terms-of-service" className={authLinkClass}>
          Terms of Service
        </a>{" "}
        and{" "}
        <a href="/privacy" className={authLinkClass}>
          Privacy Policy
        </a>
      </p>
    </AuthLayout>
  );
};

export default Signup;